import {
  UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField,
  showNotification,
} from 'platform/components';
import {Pattern, match} from 'ts-pattern';

import {useEffect, useRef, useState} from 'react';

import {UploadFileResponse} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {UploadFileError, useCustomUpload} from '@dms/shared';

interface UseSaleVehicleSimpleAlbumProps {
  onUploadStart?: (file: File) => void;
  onFileUploadComplete?: (id: string, uploadData: UploadFileResponse, file: File) => void;
  onQueueProcessed?: (uploadedFiles: UploadFileResponse[]) => void;
  onUploadError?: (error: UploadFileError | ProgressEvent, file: File) => void;
}

/**
 * Simplified hook for uploading files to a sale vehicle album.
 */
export const useSaleVehicleSimpleAlbum = (props: UseSaleVehicleSimpleAlbumProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFile] = useCustomUpload();
  const uploadQueue = useRef<Map<string, Promise<UploadFileResponse | undefined>>>(new Map());
  const [queuedFiles, setQueuedFiles] = useState<File[]>([]);

  const {onUploadStart, onFileUploadComplete, onQueueProcessed, onUploadError} = props;

  useEffect(
    function processUploadQueue() {
      if (uploadQueue.current.size > 0 && uploadQueue.current.size === queuedFiles.length) {
        const queue = Array.from(uploadQueue.current.values());
        const uploadedFiles: UploadFileResponse[] = [];

        Promise.allSettled(queue)
          .then(() => {
            queuedFiles.forEach(async (file) => {
              if (uploadQueue.current.has(file.name)) {
                const data = await uploadQueue.current.get(file.name);
                if (data) {
                  uploadedFiles.push(data);
                  onFileUploadComplete?.(data?.fileId, data, file);
                }
                uploadQueue.current.delete(file.name);
              }
            });
          })
          .then(() => {
            setIsUploading(false);
            onQueueProcessed?.(uploadedFiles);
            uploadQueue.current.clear();
            setQueuedFiles([]);
          });
      }
    },
    [queuedFiles, onFileUploadComplete, onQueueProcessed]
  );

  const uploadFileToAlbum = (
    file: File,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: UploadFileResponse) => void;
      onError?: (error: UploadFileError | ProgressEvent) => void;
    }
  ) => {
    setIsUploading(true);
    onUploadStart?.(file);
    setQueuedFiles((prev) => [...prev, file]);

    const uploadPromise = new Promise<UploadFileResponse | undefined>((resolve, reject) => {
      uploadFile({
        file,
        onError: (error) => {
          onUploadError?.(error, file);
          handleUploadError(error, {}, file);
          setIsUploading(false);
          onError?.(error);
          reject();
        },
        onSuccess: (data) => {
          onSuccess?.(data);
          resolve(data);
        },
      });
    });

    uploadQueue.current.set(file.name, uploadPromise);
  };

  const handleUploadError = (
    error: UploadFileError | ProgressEvent | Error | string,
    _ret: Record<string, unknown>,
    file: File
  ) => {
    match({error})
      .with(
        {
          error: Pattern.instanceOf(UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField),
        },
        () => {
          showNotification.error(
            i18n.t('entity.photo.notifications.imageFormatIsNotAllowed', {
              name: file.name,
              mimeFormat: file.type,
            })
          );
        }
      )
      .otherwise(() => {
        showNotification.error(
          i18n.t('entity.photo.notifications.uploadFailed', {
            name: file.name,
          })
        );
      });
  };

  return {
    uploadFileToAlbum,
    isUploading,
    handleUploadError,
  };
};
